// THE single place date/time values are formatted for display: store UTC,
// render local, and put these helpers in @sportsmet/ui and @sportsmet/errors
// so the en-US assumption is stated once. Lives here for the same reason
// `money.ts` does — `@sportsmet/ui` doesn't exist yet at this build phase and
// re-export from here once it does, rather than reimplementing.
//
// Every stored instant is UTC (`timestamptz`). Every rendered instant is the
// VIEWER's own timezone, wherever in the world they are (owner decision
// Q271) — falling back to `America/New_York` only when no viewer zone is
// known. `assertAllowedTimeZone` enforces that a timezone string is a real
// IANA zone (or the literal `UTC`) at the one place a caller can supply one;
// it no longer restricts the zone to `America/*`.

import { createZonedFormatterCache } from "./zonedFormatter";

export const DEFAULT_TIME_ZONE = "America/New_York";

// `UTC`, or a `Region/City` / `Region/Sub/City` IANA shape, followed by a
// real `Intl.DateTimeFormat` existence check — the same two-step algorithm
// as events-api's `isSupportedTimeZone` and mobile's `isUsableTimeZone`, so
// the three validators on this one path agree. That combination rejects
// bare offsets (`+05:00`) and abbreviations (`EST`, `GMT`) that
// `new Intl.DateTimeFormat` would otherwise silently accept — see the
// money-and-time rule for why a bare offset can never survive a DST
// transition — while still accepting every current IANA identifier,
// canonical or not (this runtime's `Intl.supportedValuesOf("timeZone")` is
// ICU's pre-canonicalization list: it is missing current primary names
// like `Europe/Kyiv`, `Asia/Ho_Chi_Minh` and `Asia/Kolkata`, so membership
// in that list is not usable as the validity check here). `Etc/GMT+5` and
// its siblings are excluded explicitly: the sign is inverted from every
// offset convention a caller would expect. Plain `Etc/*` aliases with no
// sign ambiguity (`Etc/UTC`, `Etc/GMT`, `Etc/Universal`, `Etc/Zulu`) are
// real zones and are let through.
const TIME_ZONE_SHAPE =
  /^[A-Za-z][A-Za-z_-]*\/[A-Za-z][A-Za-z_-]*(?:\/[A-Za-z][A-Za-z_-]*)?$/;
const ETC_OFFSET_SHAPE = /^Etc\/GMT[+-]/;

// One formatter per zone and option set, never one per call (ISSUE-1136, see zonedFormatter.ts).
const zoneCheckFormatterFor = createZonedFormatterCache({});
const dateTimeFormatterFor = createZonedFormatterCache({
  dateStyle: "medium",
  timeStyle: "short",
});
const dateFormatterFor = createZonedFormatterCache({ dateStyle: "medium" });
const timeFormatterFor = createZonedFormatterCache({ timeStyle: "short" });

function isRealTimeZone(timeZone: string): boolean {
  if (timeZone === "") return false;
  if (timeZone === "UTC") return true;
  if (ETC_OFFSET_SHAPE.test(timeZone) || !TIME_ZONE_SHAPE.test(timeZone)) {
    return false;
  }
  try {
    zoneCheckFormatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

function assertAllowedTimeZone(timeZone: string): string {
  if (!isRealTimeZone(timeZone)) {
    throw new TypeError(`Not a valid IANA timezone, got "${timeZone}".`);
  }
  return timeZone;
}

export type FormattableInstant = Date | string | number;

function toInstant(value: FormattableInstant): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`Not a valid instant: ${JSON.stringify(value)}`);
  }
  return date;
}

export type FormatInstantOptions = {
  /** Viewer's IANA zone, e.g. `Europe/London`, or `UTC`. Defaults to `America/New_York`. */
  timeZone?: string;
};

/** `"Aug 5, 2026, 2:00 PM"` — American date + 12-hour time, viewer's zone. */
export function formatDateTime(
  instant: FormattableInstant,
  options: FormatInstantOptions = {},
): string {
  const timeZone = assertAllowedTimeZone(options.timeZone ?? DEFAULT_TIME_ZONE);
  return dateTimeFormatterFor(timeZone).format(toInstant(instant));
}

/** `"Aug 5, 2026"`. */
export function formatDate(
  instant: FormattableInstant,
  options: FormatInstantOptions = {},
): string {
  const timeZone = assertAllowedTimeZone(options.timeZone ?? DEFAULT_TIME_ZONE);
  return dateFormatterFor(timeZone).format(toInstant(instant));
}

/** `"2:00 PM"`. */
export function formatTime(
  instant: FormattableInstant,
  options: FormatInstantOptions = {},
): string {
  const timeZone = assertAllowedTimeZone(options.timeZone ?? DEFAULT_TIME_ZONE);
  return timeFormatterFor(timeZone).format(toInstant(instant));
}

/**
 * `"2h ago"`. Relative and therefore timezone-free — never call this for an
 * absolute date. Parses defensively instead of reusing `toInstant()`, because
 * a thrown error would blank a whole session list over one bad timestamp.
 */
export function formatRelativeTime(
  instant: FormattableInstant,
  nowMs: number,
): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  const instantMs = date.getTime();
  if (Number.isNaN(instantMs)) return "a while ago";

  const diffMs = nowMs - instantMs;
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  if (diffMs < 604_800_000) return `${Math.floor(diffMs / 86_400_000)}d ago`;
  return `${Math.floor(diffMs / 604_800_000)}w ago`;
}
